/**
 * DJ1866Page — Crédito IEPD por compras de petróleo diésel.
 *
 * Detección automática: las líneas de compras donde el producto o la cuenta
 * de gasto efectiva tienen el flag "Combustible Diésel (IEPD)" se incluyen
 * en la DJ del año comercial seleccionado.
 *
 * Para configurar productos/cuentas:
 *  - Productos / Servicios → editar producto → marcar "Combustible Diésel (IEPD)".
 *  - Plan de Cuentas → editar cuenta → marcar "Combustible Diésel (IEPD)".
 */
import { useMemo, useState } from 'react'
import { Download, Eye, Info, AlertCircle } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'

import apiClient from '@/lib/apiClient'
import DownloadButton from '@/components/DownloadButton'

type DJ1866Item = {
  supplier_rut: string
  doc_type: number
  folio: string
  issue_date: string | null
  net: string
  iva: string
  iepd: string
}

type DJ1866Preview = {
  year: number
  items: DJ1866Item[]
  totals: { net: string; iva: string; iepd: string; count: number }
}

function fmtCLP(value: string | number) {
  const n = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(n)) return '—'
  return n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })
}

function docTypeLabel(t: number): string {
  return ({
    33: 'Factura Afecta',
    34: 'Factura Exenta',
    46: 'Factura de Compra',
    56: 'ND',
    61: 'NC',
  } as Record<number, string>)[t] ?? String(t)
}

export function DJ1866Page() {
  const currentYear = new Date().getFullYear()
  const [year, setYear] = useState(currentYear - 1)
  const [preview, setPreview] = useState<DJ1866Preview | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const years = useMemo(
    () => Array.from({ length: 8 }, (_, i) => currentYear - i),
    [currentYear],
  )

  const onPreview = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await apiClient.get<DJ1866Preview>(`/accounting/dj-1866/${year}/preview/`)
      setPreview(data)
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Error al cargar previsualización')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      {/* Título/subtítulo los provee AccountingPage (PageHeader). */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-3">
          <label htmlFor="dj1866page-ano-comercial" className="text-sm text-content-muted">Año comercial</label>
          <select id="dj1866page-ano-comercial"
            value={year}
            onChange={(e) => { setYear(parseInt(e.target.value)); setPreview(null) }}
            className="px-3 py-2 text-sm border border-divider rounded-lg bg-surface text-content"
          >
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            type="button"
            onClick={onPreview}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-divider rounded-lg hover:bg-surface-hover disabled:opacity-50"
          >
            <Eye size={14} /> {loading ? 'Cargando...' : 'Previsualizar'}
          </button>
          <DownloadButton path={`/accounting/dj-1866/${year}/export/`} filename={`DJ1866_${year}.csv`} />
        </div>
      </div>

      <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-4 text-sm text-content-subtle flex gap-3 shadow-card">
        <Info size={16} className="shrink-0 mt-0.5 text-primary" />
        <div className="space-y-1">
          <p>
            Marca los productos de combustible en{' '}
            <a href="/products" className="text-primary hover:underline">Productos / Servicios</a>
            {' '}o las cuentas de gasto correspondientes en{' '}
            <a href="/accounting/chart" className="text-primary hover:underline">Plan de Cuentas</a>
            {' '}para que sus líneas se detecten automáticamente.
          </p>
          <p className="text-xs">
            El crédito IEPD reportado por defecto equivale al IVA de las líneas detectadas.
            El contador puede ajustar el monto efectivo según la tasa UTM/m³ vigente por período.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-danger bg-danger/10 p-4 text-sm text-danger flex gap-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {preview && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Documentos" value={String(preview.totals.count)} />
            <StatCard label="Neto Combustible" value={fmtCLP(preview.totals.net)} />
            <StatCard label="IVA" value={fmtCLP(preview.totals.iva)} />
            <StatCard label="Crédito IEPD" value={fmtCLP(preview.totals.iepd)} />
          </div>

          {preview.items.length === 0 ? (
            <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-8 text-center text-content-muted shadow-card">
              No se detectaron compras de combustible diésel para el año {preview.year}.
            </div>
          ) : (
            <DataTable
              columns={[
                { key: 'supplier_rut', header: 'RUT Proveedor' },
                { key: 'doc_type', header: 'Tipo Doc', render: it => <span className="text-content-subtle">{docTypeLabel(it.doc_type)}</span> },
                { key: 'folio', header: 'Folio' },
                { key: 'issue_date', header: 'Fecha', render: it => <span className="text-content-subtle">{it.issue_date || '—'}</span> },
                { key: 'net', header: 'Neto', numeric: true, sortValue: it => Number(it.net), render: it => fmtCLP(it.net) },
                { key: 'iva', header: 'IVA', numeric: true, sortValue: it => Number(it.iva), render: it => fmtCLP(it.iva) },
                { key: 'iepd', header: 'IEPD', numeric: true, sortValue: it => Number(it.iepd), render: it => fmtCLP(it.iepd) },
              ]}
              rows={preview.items}
              rowKey={(_, i) => i}
              minWidth={820}
              caption="Detalle de la declaración jurada 1866"
            />
          )}
        </div>
      )}
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card border border-divider bg-glass backdrop-blur-glass p-4 shadow-card">
      <div className="text-xs text-content-muted">{label}</div>
      <div className="text-lg font-bold text-content mt-1 tabular-nums">{value}</div>
    </div>
  )
}

export default DJ1866Page
